import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import t from "@/lang/lang";
import {
  useGfsCatalogGetQuery,
  useGfsCatalogItemDeleteMutation,
  useGfsCatalogItemPutMutation,
  useGfsCatalogPutMutation,
  useGfsCatalogConsumableDeleteMutation,
  useGfsCatalogConsumablePutMutation,
} from "@/services/backend/gfs/catalog/api";
import { usePestcontrolMonitorListQuery } from "@/services/backend/pestcontrol/api/monitor-api";
import { usePestcontrolPestListQuery } from "@/services/backend/pestcontrol/api/pest-api";
import { PestcontrolRowActions } from "@/services/backend/pestcontrol/components/row-actions";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  DataTable,
  DataTableContainer,
  DataTableFilters,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import {
  comparePests,
  formatDuration,
  formatMonitorType,
  groupsFromPests,
  pestToSnapshot,
} from "@/services/backend/pestcontrol/lib/monitor";
import { PestcontrolMonitorType } from "@/services/backend/pestcontrol/types/monitor";
import {
  Pest,
  PestKind,
  PestSnapshot,
} from "@/services/backend/pestcontrol/types/pest";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { GfsOfferHtmlEditor } from "@/services/backend/gfs/offer/components/offer-html-editor";
import {
  GfsCatalogItem,
  GfsConsumable,
  formatGfsConsumableDisplayName,
} from "@/services/backend/gfs/catalog/types";
import {
  formatGfsUnit,
  GfsCatalogUnitOptions,
  GfsUnit,
  GfsUnitValue,
  normalizeGfsUnit,
} from "@/services/backend/gfs/unit";
import { ColumnDef } from "@tanstack/react-table";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

type CatalogMonitorOrderBy =
  "name" | "type" | "price" | "installationTime" | "serviceTime";

type CatalogMonitorListRequest = {
  name: Filter<string>;
  type: Filter<PestcontrolMonitorType>;
  orderBy: OrdrBy<CatalogMonitorOrderBy>[];
};

export function GfsCatalogDetail({ catalogId }: { catalogId: string }) {
  const { data, error, isLoading } = useGfsCatalogGetQuery({ id: catalogId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || !data) {
    return (
      <div className="text-muted-foreground p-4 text-sm">
        {t("Lade Katalog ...")}
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <CatalogInfo catalog={data} />
      <CatalogMonitorList catalogId={data.id} />
    </div>
  );
}

export function GfsCatalogConsumables({
  catalogId,
  consumables,
}: {
  catalogId: string;
  consumables: GfsConsumable[];
}) {
  const { companyId } = useActiveCompany();
  const [putConsumable, putState] = useGfsCatalogConsumablePutMutation();
  const [deleteConsumable, deleteState] =
    useGfsCatalogConsumableDeleteMutation();
  const { data: pestData } = usePestcontrolPestListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
    offset: 0,
  });
  const pests = useMemo(
    () => [...(pestData?.items ?? [])].sort(comparePests),
    [pestData?.items],
  );
  const [detailsDraft, setDetailsDraft] = useState<GfsConsumable | null>(null);
  const [request, setRequest] = useState<CatalogConsumableListRequest>({
    name: { active: false, mode: FilterMode.Like, values: [] },
    orderBy: [{ field: "name" }],
  });
  const filteredConsumables = useMemo(
    () => filterCatalogConsumables(consumables, request),
    [consumables, request],
  );
  const editDraft = useCallback(
    (consumable: GfsConsumable) => setDetailsDraft(consumable),
    [],
  );
  const remove = useCallback(
    async (consumable: GfsConsumable) => {
      await deleteConsumable({
        catalogId,
        consumableId: consumable.id,
      }).unwrap();
      setDetailsDraft((current) =>
        current?.id === consumable.id ? null : current,
      );
    },
    [catalogId, deleteConsumable],
  );
  const saveDetails = useCallback(
    async (consumable: GfsConsumable) => {
      if (
        !consumable.name.trim() ||
        !consumable.type.trim() ||
        !consumable.unit.trim()
      ) {
        return;
      }
      await putConsumable({
        catalogId,
        consumable: {
          ...consumable,
          name: consumable.name.trim(),
          type: consumable.type.trim(),
          unit: normalizeGfsUnit(consumable.unit.trim()),
          productDisplayText: consumable.productDisplayText ?? "",
        },
      }).unwrap();
      setDetailsDraft(null);
    },
    [catalogId, putConsumable],
  );
  const columns = useCatalogConsumableColumns({
    onEdit: editDraft,
    onDelete: remove,
  });
  const table = useDataTableController(columns, filteredConsumables);

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">
            {t("Verbrauchsmaterialien")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(
              "Verbrauchsmaterialien können auch ohne Monitor verwendet werden.",
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={putState.isLoading || deleteState.isLoading}
          onClick={() =>
            setDetailsDraft({
              id: v4(),
              name: "",
              type: "",
              unit: GfsUnit.Piece,
              unitPrice: 0,
              productDisplayText: "",
              pests: [],
              groups: [],
            })
          }
        >
          <Plus className="h-4 w-4" />
          {t("Verbrauchsmaterial hinzufügen")}
        </Button>
      </div>
      <DataTableContainer>
        <DataTableFilters className="flex flex-wrap items-center gap-2">
          <SearchFilter
            className="h-9 w-[230px]"
            filter={request.name}
            onFilterChange={(name) => setRequest({ ...request, name })}
            placeholder={t("Nach Name suchen ...")}
          />
          <OrderBySelect<CatalogConsumableOrderBy>
            orderBy={request.orderBy}
            onOrderByChange={(orderBy) => setRequest({ ...request, orderBy })}
            fields={[
              { label: t("Name"), value: "name" },
              { label: t("Art"), value: "type" },
              { label: t("Einheit"), value: "unit" },
              { label: t("Preis"), value: "unitPrice" },
            ]}
          />
        </DataTableFilters>
        <DataTable columns={columns} table={table} />
      </DataTableContainer>
      <ConsumableDetailsDialog
        consumable={detailsDraft}
        disabled={putState.isLoading || deleteState.isLoading}
        isNew={
          detailsDraft !== null &&
          !consumables.some((consumable) => consumable.id === detailsDraft.id)
        }
        pests={pests}
        onOpenChange={(open) => {
          if (!open) setDetailsDraft(null);
        }}
        onSave={saveDetails}
      />
      {putState.error && <RTKQueryErrorAlert error={putState.error} />}
      {deleteState.error && <RTKQueryErrorAlert error={deleteState.error} />}
    </div>
  );
}

type CatalogConsumableOrderBy = "name" | "type" | "unit" | "unitPrice";

type CatalogConsumableListRequest = {
  name: Filter<string>;
  orderBy: OrdrBy<CatalogConsumableOrderBy>[];
};

const consumableUnitOptions = GfsCatalogUnitOptions;

function useCatalogConsumableColumns({
  onEdit,
  onDelete,
}: {
  onEdit: (consumable: GfsConsumable) => void;
  onDelete: (consumable: GfsConsumable) => Promise<void>;
}): ColumnDef<GfsConsumable>[] {
  return useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <span
            className="block max-w-[22rem] truncate"
            title={formatGfsConsumableDisplayName(row.original)}
          >
            {formatGfsConsumableDisplayName(row.original)}
          </span>
        ),
      },
      {
        header: t("Art"),
        accessorKey: "type",
        cell: ({ row }) => formatConsumableType(row.original.type),
      },
      {
        header: t("Einheit"),
        accessorKey: "unit",
        cell: ({ row }) => formatConsumableUnit(row.original.unit),
      },
      {
        header: t("Zielorganismen"),
        accessorKey: "pests",
        cell: ({ row }) => formatConsumablePests(row.original.pests),
      },
      {
        header: t("Schädlingsgruppen"),
        accessorKey: "groups",
        cell: ({ row }) =>
          formatConsumableGroups(row.original.groups, row.original.pests),
      },
      {
        header: () => <div className="text-end">{t("Preis")}</div>,
        accessorKey: "unitPrice",
        cell: ({ row }) => (
          <div className="text-end tabular-nums">
            {formatConsumablePrice(row.original.unitPrice)}
          </div>
        ),
      },
      {
        id: "actions",
        header: () => <div className="text-end">{t("Aktion")}</div>,
        cell: ({ row }) => (
          <PestcontrolRowActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
          />
        ),
      },
    ],
    [onDelete, onEdit],
  );
}

function ConsumableDetailsDialog({
  consumable,
  disabled,
  isNew,
  pests,
  onOpenChange,
  onSave,
}: {
  consumable: GfsConsumable | null;
  disabled: boolean;
  isNew: boolean;
  pests: Pest[];
  onOpenChange: (open: boolean) => void;
  onSave: (consumable: GfsConsumable) => Promise<void>;
}) {
  const [draft, setDraft] = useState<GfsConsumable | null>(consumable);

  useEffect(() => {
    setDraft(consumable);
  }, [consumable]);

  const update = (change: Partial<GfsConsumable>) =>
    setDraft((current) => (current ? { ...current, ...change } : current));

  const save = async () => {
    if (!draft) return;
    await onSave(draft);
  };

  return (
    <Dialog open={consumable !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t("Verbrauchsmaterial anlegen")
              : t("Verbrauchsmaterial bearbeiten")}
          </DialogTitle>
        </DialogHeader>
        {draft && (
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>{t("Name")}</Label>
              <Input
                value={draft.name}
                onChange={(event) => update({ name: event.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="grid gap-1.5">
                <Label>{t("Art")}</Label>
                <Input
                  value={draft.type}
                  onChange={(event) => update({ type: event.target.value })}
                  disabled={disabled}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("Einheit")}</Label>
                <Select
                  value={normalizeGfsUnit(draft.unit)}
                  onValueChange={(unit) => update({ unit })}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("Einheit auswählen")} />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptionsFor(draft.unit).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>{t("Preis je Einheit")}</Label>
                <div className="flex items-center justify-end gap-1">
                  <FloatNumberInput
                    value={draft.unitPrice}
                    onChange={(unitPrice) => update({ unitPrice })}
                    disabled={disabled}
                  />
                  <span className="text-muted-foreground text-sm">€</span>
                </div>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Präparateanzeigetext")}</Label>
              <p className="text-muted-foreground text-xs">
                {t(
                  "Dieser formatierte Text wird im Abschnitt Präparate angezeigt. Der Anzeigename in der Kalkulation bleibt unverändert.",
                )}
              </p>
              <GfsOfferHtmlEditor
                value={draft.productDisplayText ?? ""}
                onChange={(productDisplayText) =>
                  update({ productDisplayText })
                }
                disabled={disabled}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Zielorganismen")}</Label>
              <ConsumablePestsEditor
                pests={pests}
                selected={draft.pests ?? []}
                disabled={disabled}
                onChange={(selected) =>
                  update({ pests: selected, groups: groupsFromPests(selected) })
                }
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={disabled}
          >
            {t("Abbrechen")}
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={
              disabled ||
              !draft?.name.trim() ||
              !draft?.type.trim() ||
              !draft?.unit.trim()
            }
          >
            {t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatConsumableType(type?: string) {
  return type?.trim() || "—";
}

function formatConsumablePrice(price: number) {
  return `${price.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function unitOptionsFor(unit: GfsUnitValue) {
  const normalized = normalizeGfsUnit(unit);
  if (consumableUnitOptions.some((option) => option.value === normalized)) {
    return consumableUnitOptions;
  }
  return unit
    ? [
        { value: normalized, label: formatGfsUnit(normalized) },
        ...consumableUnitOptions,
      ]
    : consumableUnitOptions;
}

function formatConsumableUnit(unit: GfsUnitValue) {
  return formatGfsUnit(unit);
}

function formatConsumablePests(pests?: PestSnapshot[]) {
  return pests?.length ? pests.map((pest) => pest.name).join(", ") : "—";
}

function formatConsumableGroups(
  groups?: GfsConsumable["groups"],
  pests?: PestSnapshot[],
) {
  const derivedGroups = groups?.length ? groups : groupsFromPests(pests ?? []);
  return derivedGroups.length
    ? derivedGroups.map((group) => group.name).join(", ")
    : "—";
}

function ConsumablePestsEditor({
  pests,
  selected,
  disabled,
  onChange,
}: {
  pests: Pest[];
  selected: PestSnapshot[];
  disabled: boolean;
  onChange: (selected: PestSnapshot[]) => void;
}) {
  const selectedIDs = new Set(selected.map((pest) => pest.sourceId));
  const addPest = (id: string) => {
    const pest = pests.find((candidate) => candidate.id === id);
    if (!pest || selectedIDs.has(pest.id)) return;
    onChange([...selected, pestToSnapshot(pest)]);
  };
  const removePest = (id: string) =>
    onChange(selected.filter((pest) => pest.sourceId !== id));

  return (
    <div className="grid min-w-[18rem] gap-1.5">
      <div className="flex flex-wrap gap-1">
        {selected.map((pest) => (
          <Button
            key={`${pest.kind}-${pest.sourceId}`}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={disabled}
            onClick={() => removePest(pest.sourceId)}
          >
            {pest.name} ×
          </Button>
        ))}
        {!selected.length && (
          <span className="text-muted-foreground text-xs">
            {t("Keine Zielorganismen")}
          </span>
        )}
      </div>
      <div className="grid gap-1">
        <ConsumablePestSelector
          kind={PestKind.Category}
          pests={pests}
          selectedIDs={selectedIDs}
          disabled={disabled}
          onSelect={addPest}
        />
        <ConsumablePestSelector
          kind={PestKind.Indicator}
          pests={pests}
          selectedIDs={selectedIDs}
          disabled={disabled}
          onSelect={addPest}
        />
      </div>
    </div>
  );
}

function ConsumablePestSelector({
  kind,
  pests,
  selectedIDs,
  disabled,
  onSelect,
}: {
  kind: PestKind;
  pests: Pest[];
  selectedIDs: Set<string>;
  disabled: boolean;
  onSelect: (id: string) => void;
}) {
  const available = pests.filter(
    (pest) => pest.kind === kind && !selectedIDs.has(pest.id),
  );
  const placeholder =
    kind === PestKind.Category
      ? t("Kategorie hinzufügen")
      : t("Indikator hinzufügen");

  return (
    <Select
      value=""
      onValueChange={onSelect}
      disabled={disabled || available.length === 0}
    >
      <SelectTrigger className="h-8 w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {available.map((pest) => (
          <SelectItem key={pest.id} value={pest.id}>
            {pest.name}
            {pest.group ? ` (${pest.group.name})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function filterCatalogConsumables(
  consumables: GfsConsumable[],
  request: CatalogConsumableListRequest,
) {
  const name = request.name.values?.[0]?.trim().toLocaleLowerCase();
  const filteredConsumables = consumables.filter(
    (consumable) =>
      !request.name.active ||
      !name ||
      consumable.name.toLocaleLowerCase().includes(name),
  );

  return [...filteredConsumables].sort((left, right) => {
    for (const order of request.orderBy) {
      const comparison = compareCatalogConsumables(left, right, order.field);
      if (comparison !== 0) return order.dsc ? -comparison : comparison;
    }
    return 0;
  });
}

function compareCatalogConsumables(
  left: GfsConsumable,
  right: GfsConsumable,
  field: CatalogConsumableOrderBy,
) {
  switch (field) {
    case "name":
      return left.name.localeCompare(right.name);
    case "type":
      return left.type.localeCompare(right.type);
    case "unit":
      return left.unit.localeCompare(right.unit);
    case "unitPrice":
      return left.unitPrice - right.unitPrice;
    default:
      return 0;
  }
}

function CatalogInfo({
  catalog,
}: {
  catalog: {
    id: string;
    companyId: string;
    customerId?: string;
    name: string;
    description: string;
    isFallback: boolean;
  };
}) {
  const [putCatalog, putCatalogState] = useGfsCatalogPutMutation();
  const [name, setName] = useState(catalog.name);
  const [description, setDescription] = useState(catalog.description);

  useEffect(() => {
    setName(catalog.name);
    setDescription(catalog.description);
  }, [catalog]);

  const saveCatalog = () =>
    putCatalog({
      id: catalog.id,
      companyId: catalog.companyId,
      customerId: catalog.customerId,
      name,
      description,
      isFallback: catalog.isFallback,
    });

  return (
    <div className="bg-background grid gap-3 rounded-md border p-4">
      <div className="grid gap-1.5">
        <Label>{t("Name")}</Label>
        <Input value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="grid gap-1.5">
        <Label>{t("Beschreibung")}</Label>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div>
        <Button
          onClick={saveCatalog}
          disabled={!name.trim() || putCatalogState.isLoading}
        >
          {t("Speichern")}
        </Button>
      </div>
      {putCatalogState.error && (
        <RTKQueryErrorAlert error={putCatalogState.error} />
      )}
    </div>
  );
}

function CatalogMonitorList({ catalogId }: { catalogId: string }) {
  const { data, error } = useGfsCatalogGetQuery({ id: catalogId });
  const [deleteItem, deleteItemState] = useGfsCatalogItemDeleteMutation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryType = searchParams.get("type");
  const initialType = isMonitorType(queryType) ? queryType : undefined;
  const [request, setRequest] = useState<CatalogMonitorListRequest>({
    name: { active: false, mode: FilterMode.Like, values: [] },
    type: initialType
      ? { active: true, values: [initialType] }
      : { active: false, values: [] },
    orderBy: [{ field: "name" }],
  });
  useEffect(() => {
    const nextType = isMonitorType(queryType) ? queryType : undefined;
    setRequest((current) => ({
      ...current,
      type: nextType
        ? { active: true, values: [nextType] }
        : { active: false, values: [] },
    }));
  }, [queryType]);
  const items = useMemo(
    () => filterCatalogItems(data?.items ?? [], request),
    [data?.items, request],
  );
  const columns = useCatalogMonitorColumns({
    onEdit: (item) => navigate(item.id),
    onDelete: (item) => deleteItem({ catalogId, itemId: item.id }).unwrap(),
  });
  const table = useDataTableController(columns, items);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{t("Monitore")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("Hier können Monitore angelegt und bearbeitet werden.")}
          </p>
        </div>
        <CreateMonitorDialog catalogId={catalogId} />
      </div>
      {deleteItemState.error && (
        <RTKQueryErrorAlert error={deleteItemState.error} />
      )}
      <DataTableContainer>
        <DataTableFilters className="flex flex-wrap items-center gap-2">
          <SearchFilter
            className="h-9 w-[230px]"
            filter={request.name}
            onFilterChange={(name) => setRequest({ ...request, name })}
            placeholder={t("Nach Name suchen ...")}
          />
          <MonitorTypeFilter
            value={request.type.values?.[0] ?? "all"}
            onChange={(type) =>
              setRequest({
                ...request,
                type:
                  type === "all"
                    ? { active: false, values: [] }
                    : { active: true, values: [type] },
              })
            }
          />
          <OrderBySelect<CatalogMonitorOrderBy>
            orderBy={request.orderBy}
            onOrderByChange={(orderBy) => setRequest({ ...request, orderBy })}
            fields={[
              { label: t("Name"), value: "name" },
              { label: t("Typ"), value: "type" },
              { label: t("Preis"), value: "price" },
              { label: t("Installationszeit"), value: "installationTime" },
              { label: t("Servicezeit"), value: "serviceTime" },
            ]}
          />
        </DataTableFilters>
        <DataTable columns={columns} table={table} />
      </DataTableContainer>
    </div>
  );
}

function isMonitorType(value: string | null): value is PestcontrolMonitorType {
  return Object.values(PestcontrolMonitorType).includes(
    value as PestcontrolMonitorType,
  );
}

function MonitorTypeFilter({
  value,
  onChange,
}: {
  value: PestcontrolMonitorType | "all";
  onChange: (value: PestcontrolMonitorType | "all") => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) =>
        onChange(next === "all" ? "all" : (next as PestcontrolMonitorType))
      }
    >
      <SelectTrigger className="h-9 w-[190px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("Alle Typen")}</SelectItem>
        {Object.values(PestcontrolMonitorType).map((type) => (
          <SelectItem key={type} value={type}>
            {formatMonitorType(type)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function filterCatalogItems(
  items: GfsCatalogItem[],
  request: CatalogMonitorListRequest,
) {
  const name = request.name.values?.[0]?.trim().toLocaleLowerCase();
  const type = request.type.values?.[0];
  const filteredItems = items.filter(
    (item) =>
      (!request.name.active ||
        !name ||
        item.monitor.name.toLocaleLowerCase().includes(name)) &&
      (!request.type.active || !type || item.monitor.type === type),
  );

  return [...filteredItems].sort((left, right) => {
    for (const order of request.orderBy) {
      const comparison = compareCatalogItems(left, right, order.field);
      if (comparison !== 0) return order.dsc ? -comparison : comparison;
    }
    return 0;
  });
}

function compareCatalogItems(
  left: GfsCatalogItem,
  right: GfsCatalogItem,
  field: CatalogMonitorOrderBy,
) {
  switch (field) {
    case "name":
      return left.monitor.name.localeCompare(right.monitor.name);
    case "type":
      return formatMonitorType(left.monitor.type).localeCompare(
        formatMonitorType(right.monitor.type),
      );
    case "price":
      return left.price - right.price;
    case "installationTime":
      return left.installationTime - right.installationTime;
    case "serviceTime":
      return left.serviceTime - right.serviceTime;
  }
}

function useCatalogMonitorColumns({
  onEdit,
  onDelete,
}: {
  onEdit: (item: GfsCatalogItem) => void;
  onDelete: (item: GfsCatalogItem) => Promise<unknown>;
}): ColumnDef<GfsCatalogItem>[] {
  return useMemo(
    () => [
      { header: t("Name"), accessorKey: "monitor.name" },
      {
        header: t("Typ"),
        cell: ({ row }) => formatMonitorType(row.original.monitor.type),
      },
      {
        header: t("Montage"),
        cell: ({ row }) => formatDuration(row.original.installationTime),
      },
      {
        header: t("Service"),
        cell: ({ row }) => formatDuration(row.original.serviceTime),
      },
      {
        header: t("Preis"),
        cell: ({ row }) => `${row.original.price.toFixed(2)} EUR`,
      },
      {
        header: t("Schädlinge"),
        cell: ({ row }) => row.original.pests.length || "-",
      },
      {
        header: t("Gruppen"),
        cell: ({ row }) => row.original.groups.length || "-",
      },
      {
        id: "actions",
        header: () => <div className="text-end">{t("Aktion")}</div>,
        cell: ({ row }) => (
          <PestcontrolRowActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
          />
        ),
      },
    ],
    [onDelete, onEdit],
  );
}

function CreateMonitorDialog({ catalogId }: { catalogId: string }) {
  const { companyId } = useActiveCompany();
  const { data: monitors } = usePestcontrolMonitorListQuery({
    company: { active: true, values: [companyId] },
    name: { active: false, mode: FilterMode.Like, values: [] },
    limit: 500,
    offset: 0,
  });
  const [open, setOpen] = useState(false);
  const [selectedMonitorId, setSelectedMonitorId] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState(PestcontrolMonitorType.Checkpoint);
  const [installationTime, setInstallationTime] = useState(0);
  const [serviceTime, setServiceTime] = useState(0);
  const [putItem, putItemState] = useGfsCatalogItemPutMutation();

  const createMonitor = async () => {
    if (!name.trim()) return;
    await putItem({
      catalogId,
      itemId: v4(),
      name: name.trim(),
      type,
      installationTime,
      serviceTime,
      consumables: [],
    }).unwrap();
    setName("");
    setInstallationTime(0);
    setServiceTime(0);
    setOpen(false);
  };

  const importMonitor = async () => {
    if (!selectedMonitorId) return;
    await putItem({
      catalogId,
      itemId: v4(),
      monitorId: selectedMonitorId,
      installationTime: 0,
      serviceTime: 0,
      consumables: [],
    }).unwrap();
    setSelectedMonitorId("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4" />
          {t("Monitor hinzufügen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Monitor hinzufügen")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6">
          <div className="grid gap-3">
            <h3 className="text-sm font-medium">
              {t("Neuen Monitor anlegen")}
            </h3>
            <div className="grid gap-1.5">
              <Label>{t("Name")}</Label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("Typ")}</Label>
              <Select
                value={type}
                onValueChange={(value) =>
                  setType(value as PestcontrolMonitorType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PestcontrolMonitorType.Checkpoint}>
                    {t("Prüfpunkt")}
                  </SelectItem>
                  <SelectItem value={PestcontrolMonitorType.FlyExterminator}>
                    {t("Fluginsektenvernichter")}
                  </SelectItem>
                  <SelectItem value={PestcontrolMonitorType.IoT}>
                    {t("IoT-Monitor")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>{t("Installationszeit")}</Label>
                <TimeNumberInput
                  value={installationTime}
                  onChange={setInstallationTime}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("Servicezeit")}</Label>
                <TimeNumberInput
                  value={serviceTime}
                  onChange={setServiceTime}
                />
              </div>
            </div>
            <Button
              className="w-fit"
              onClick={createMonitor}
              disabled={!name.trim() || putItemState.isLoading}
            >
              {t("Anlegen")}
            </Button>
          </div>
          <div className="grid gap-3 border-t pt-4">
            <h3 className="text-sm font-medium">
              {t("Aus Pestcontrol übernehmen")}
            </h3>
            <div className="grid gap-1.5">
              <Label>{t("Monitor")}</Label>
              <Select
                value={selectedMonitorId}
                onValueChange={setSelectedMonitorId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("Monitor auswählen")} />
                </SelectTrigger>
                <SelectContent>
                  {(monitors?.items ?? []).map((monitor) => (
                    <SelectItem key={monitor.id} value={monitor.id}>
                      {monitor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-fit"
              variant="outline"
              onClick={importMonitor}
              disabled={!selectedMonitorId || putItemState.isLoading}
            >
              {t("Übernehmen")}
            </Button>
          </div>
          {putItemState.error && (
            <RTKQueryErrorAlert error={putItemState.error} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
